import { MapPin, Clock, CheckCircle2, Circle } from 'lucide-react';
import { cn } from '@paulownia/shared/lib/utils';
import type { Market } from '@paulownia/shared/types';

interface MarketCardProps {
  market: Market;
  isSelected: boolean;
  isDisabled: boolean;
  onToggle: () => void;
}

/**
 * Card component for displaying a market with selection checkbox.
 * Used in the market selection step of onboarding.
 */
export function MarketCard({
  market,
  isSelected,
  isDisabled,
  onToggle,
}: MarketCardProps) {
  const canSelect = !isDisabled || isSelected;

  return (
    <button
      type="button"
      onClick={canSelect ? onToggle : undefined}
      disabled={!canSelect}
      className={cn(
        'w-full text-left p-4 rounded-lg border transition-all',
        'focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1',
        isSelected
          ? 'border-orange-500 bg-orange-50'
          : isDisabled
            ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
            : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
      )}
    >
      <div className="flex items-start gap-3">
        {/* Checkbox icon */}
        <div className="flex-shrink-0 mt-0.5">
          {isSelected ? (
            <CheckCircle2 className="w-5 h-5 text-orange-600" />
          ) : (
            <Circle
              className={cn(
                'w-5 h-5',
                isDisabled ? 'text-gray-300' : 'text-gray-400'
              )}
            />
          )}
        </div>

        {/* Market info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3
              className={cn(
                'font-medium truncate',
                isSelected ? 'text-orange-900' : 'text-gray-900'
              )}
            >
              {market.city}
            </h3>
            {isDisabled && !isSelected && (
              <span className="flex-shrink-0 text-xs font-medium text-amber-600 bg-amber-100 px-2 py-0.5 rounded">
                Limite
              </span>
            )}
          </div>

          {/* Address */}
          <div
            className={cn(
              'flex items-center gap-1.5 mt-1 text-sm',
              isSelected ? 'text-orange-700' : 'text-gray-500'
            )}
          >
            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">{market.address}</span>
          </div>

          {/* Opening hours */}
          <div
            className={cn(
              'flex items-center gap-1.5 mt-1 text-sm',
              isSelected ? 'text-orange-700' : 'text-gray-500'
            )}
          >
            <Clock className="w-3.5 h-3.5 flex-shrink-0" />
            <span>
              {market.openingTime} - {market.closingTime}
            </span>
          </div>
        </div>
      </div>
    </button>
  );
}
